import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AdminLiveSessionService, MonitorSession } from '@shared/services/live-sessions';
import { interval, Subscription, switchMap } from 'rxjs';
import { ToastService } from 'thkee-common';

@Component({
  selector: 'app-admin-monitor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="space-y-4">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <h4 class="text-lg font-semibold text-neutral-800" i18n>Session Monitor</h4>
          <span class="text-sm text-neutral-500">{{ sessions().length }} <span i18n>sessions</span></span>
        </div>
        <div class="flex items-center gap-2 text-sm text-neutral-500">
          <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span i18n>Auto-refresh 30s</span>
          <span class="ml-2">{{ currentTime() | date:'HH:mm:ss' }}</span>
        </div>
      </div>

      @if (isLoading() && sessions().length === 0) {
        <div class="flex items-center justify-center py-12">
          <p class="text-neutral-400" i18n>Loading monitor data...</p>
        </div>
      } @else if (sessions().length === 0) {
        <div class="flex items-center justify-center py-12">
          <div class="text-center">
            <p class="text-neutral-400 text-lg" i18n>No sessions in the monitoring window</p>
            <p class="text-neutral-400 text-sm mt-1" i18n>Sessions starting within 2 hours or currently live will appear here</p>
          </div>
        </div>
      } @else {
        <!-- Session Cards Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          @for (session of sortedSessions(); track session.id) {
            <div class="rounded-lg p-4 transition-all" [class]="getUrgencyClass(session)">
              <!-- Course Title -->
              <h5 class="font-semibold text-neutral-800 text-sm line-clamp-2 mb-2">{{ session.course_title }}</h5>

              <!-- Instructor -->
              <div class="flex items-center gap-2 text-sm mb-3">
                <span class="text-neutral-600" i18n>Instructor:</span>
                <span class="font-medium text-neutral-800">{{ session.instructor_name }}</span>
                @if (session.instructor_phone) {
                  <a [href]="'tel:' + session.instructor_phone" class="text-blue-600 text-xs underline">{{ session.instructor_phone }}</a>
                }
              </div>

              <!-- Stats Row -->
              <div class="grid grid-cols-3 gap-2 mb-3">
                <div class="bg-white/60 rounded p-2 text-center">
                  <p class="text-xs text-neutral-500" i18n>Start</p>
                  <p class="text-sm font-medium">{{ session.start_datetime | date:'HH:mm' }}</p>
                </div>
                <div class="bg-white/60 rounded p-2 text-center">
                  <p class="text-xs text-neutral-500" i18n>Duration</p>
                  <p class="text-sm font-medium">{{ session.duration }}min</p>
                </div>
                <div class="bg-white/60 rounded p-2 text-center">
                  <p class="text-xs text-neutral-500" i18n>Status</p>
                  <p class="text-sm font-medium">{{ getStatusLabel(session.temporal_status) }}</p>
                </div>
              </div>

              <!-- Enrollment & Attendance -->
              <div class="flex items-center justify-between text-sm mb-3">
                <span class="text-neutral-600">
                  <span i18n>Enrolled:</span> {{ session.enrolled_count }}{{ session.max_participants ? '/' + session.max_participants : '' }}
                </span>
                <span class="text-neutral-600">
                  <span i18n>Attendees:</span> {{ session.students_present_count }}
                </span>
              </div>

              <!-- Live Status / Countdown -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  @if (session.is_video_active) {
                    <span class="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></span>
                    @if (session.is_instructor_missing) {
                      <span class="text-red-600 text-xs font-medium" i18n>No Instructor!</span>
                    } @else {
                      <span class="text-green-700 text-xs font-medium" i18n>Live</span>
                    }
                  } @else {
                    <span class="text-neutral-500 text-xs">{{ getTimeLabel(session) }}</span>
                  }
                </div>

                @if (session.is_video_active) {
                  <button
                    (click)="joinSession(session)"
                    class="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    i18n>
                    Join
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  `,
  imports: [DatePipe],
})
export class AdminMonitorComponent implements OnInit, OnDestroy {
  private readonly adminService = inject(AdminLiveSessionService);
  private readonly toastService = inject(ToastService);

  sessions = signal<MonitorSession[]>([]);
  sortedSessions = signal<MonitorSession[]>([]);
  isLoading = signal(false);
  currentTime = signal(new Date());

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.loadMonitorData();

    // Auto-refresh every 30 seconds
    this.subscriptions.add(
      interval(30000)
        .pipe(switchMap(() => this.adminService.getMonitorSessions()))
        .subscribe({
          next: (sessions) => {
            this.sessions.set(sessions);
            this.sortedSessions.set(this.sortSessions(sessions));
          },
          error: () => {
            this.toastService.error({ message: 'Failed to refresh monitor data' });
          },
        })
    );

    // Clock tick every second
    this.subscriptions.add(
      interval(1000).subscribe(() => {
        this.currentTime.set(new Date());
      })
    );
  }

  loadMonitorData(): void {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.adminService.getMonitorSessions().subscribe({
        next: (sessions) => {
          this.sessions.set(sessions);
          this.sortedSessions.set(this.sortSessions(sessions));
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.toastService.error({ message: 'Failed to load monitor data' });
        },
      })
    );
  }

  private sortSessions(sessions: MonitorSession[]): MonitorSession[] {
    return [...sessions].sort((a, b) => {
      const priorityA = this.getSortPriority(a);
      const priorityB = this.getSortPriority(b);
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime();
    });
  }

  private getSortPriority(session: MonitorSession): number {
    if (session.is_video_active && session.is_instructor_missing) return 0; // Alert
    if (session.is_video_active) return 1; // Live
    if (session.temporal_status === 'upcoming') return 2; // Starting soon
    return 3; // Recently ended
  }

  getUrgencyClass(session: MonitorSession): string {
    if (session.is_video_active && session.is_instructor_missing) {
      return 'ring-2 ring-red-500 bg-red-50 animate-pulse';
    }
    if (session.is_video_active) {
      return 'ring-2 ring-green-500 bg-green-50';
    }
    if (session.temporal_status === 'upcoming') {
      const startTime = new Date(session.start_datetime).getTime();
      const now = Date.now();
      const minutesUntilStart = (startTime - now) / 60000;
      if (minutesUntilStart <= 30) {
        return 'ring-1 ring-amber-400 bg-amber-50';
      }
    }
    if (session.temporal_status === 'completed') {
      return 'opacity-60 bg-neutral-50';
    }
    return 'bg-white border border-neutral-200';
  }

  getTimeLabel(session: MonitorSession): string {
    const now = Date.now();
    const startTime = new Date(session.start_datetime).getTime();
    const endTime = new Date(session.end_datetime).getTime();

    if (session.temporal_status === 'upcoming') {
      const minutesUntilStart = Math.round((startTime - now) / 60000);
      if (minutesUntilStart <= 0) return 'Starting now';
      if (minutesUntilStart < 60) return `Starts in ${minutesUntilStart} min`;
      const hours = Math.floor(minutesUntilStart / 60);
      const mins = minutesUntilStart % 60;
      return `Starts in ${hours}h ${mins}m`;
    }
    if (session.temporal_status === 'completed') {
      const minutesSinceEnd = Math.round((now - endTime) / 60000);
      if (minutesSinceEnd < 60) return `Ended ${minutesSinceEnd} min ago`;
      return `Ended ${Math.floor(minutesSinceEnd / 60)}h ago`;
    }
    return '';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'upcoming':
        return 'Upcoming';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }

  joinSession(session: MonitorSession): void {
    this.subscriptions.add(
      this.adminService.adminJoinSession(session.id).subscribe({
        next: (res) => {
          sessionStorage.setItem('admin_rtk_token', res.auth_token);
          sessionStorage.setItem('admin_rtk_room', res.room_name);
          window.open(`/live-sessions/meeting/${session.id}`, '_blank');
        },
        error: (err) => {
          this.toastService.error({ message: err?.error?.error || 'Failed to join session' });
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
